// @ts-strict-ignore
import Space from 'antd/lib/space';
import * as React from 'react';
import {
    AmazonAppStoreState,
    AppleAppStoreState,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreReviewSubmissionState,
    AppStoreState,
    AppStoreType,
    CIBuildInfo,
    GooglePlayReleaseLifecycleState,
    GooglePlayStoreState,
    HuaweiAppStoreState,
    RegressionBuildInfo,
    RegressionStatus,
    ReleaseCandidateBuildInfo,
    SamsungAppStoreState,
    ReleaseStepStatus,
    QAHistoryItem,
    BuildMatchingWarning,
} from '@/types';
import {
    releaseStepDetailsContainerModuleStyle,
    releaseStepModuleHeaderStyle,
    releaseStepModuleTitleStyle,
} from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import {
    displayConsoleNameForAppStoreType,
    displayConsoleNameWithPrefix,
    displayStoreNameForAppStoreType,
} from '../helpers/displayHelpers';
import AppStoreBuilds from './AppStoreBuilds';
import { getIsGooglePlayStore, isAppReleased, isAppSubmitted } from '../helpers';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import ChangeSelectedBuildModal from './selectedAppStoreBuild/ChangeSelectedBuildModal';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { selectIsReleaseRollback } from '../selectors/selectors';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import Dropdown from 'antd/lib/dropdown';
import ChangeAdditionalBuildsModal from '@/components/selectedAppStoreBuild/ChangeAdditionalBuildsModal';
import Button from 'antd/lib/button';

interface SelectedAppStoreBuildModuleProps extends ReleaseStepComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    appStoreState?: AppStoreState;
    googleReleaseLifecycleState?: GooglePlayReleaseLifecycleState | '';
    reviewSubmissionState?: AppStoreReviewSubmissionState;
    showChangeSelectedBuildCTA?: boolean;
    regressionBuildInfo?: RegressionBuildInfo;
    regressionStatus?: RegressionStatus;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    stepStatus?: ReleaseStepStatus;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    isAppStoreBuildListFilteredByConfiguredTrack?: boolean;
    buildMatchingWarnings?: BuildMatchingWarning[];
    willAutoPromotePreviousRelease?: boolean;
}

const SelectedAppStoreBuildModule: React.FC<SelectedAppStoreBuildModuleProps> = (
    props: SelectedAppStoreBuildModuleProps,
) => {
    return (
        <div style={{ ...releaseStepDetailsContainerModuleStyle }}>
            <SelectedAppStoreBuildModuleBase {...props} />
        </div>
    );
};

export const SelectedAppStoreBuildModuleEmbedded: React.FC<SelectedAppStoreBuildModuleProps> = (
    props: SelectedAppStoreBuildModuleProps,
) => {
    return (
        <div style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}>
            <SelectedAppStoreBuildModuleBase {...props} />
        </div>
    );
};

export const SelectedAppStoreBuildModuleBase: React.FC<SelectedAppStoreBuildModuleProps> = (
    props: SelectedAppStoreBuildModuleProps,
) => {
    const [selectBuildModalVisible, setSelectBuildModalVisible] = React.useState(false);
    const [additionalBuildsModalVisible, setAdditionalBuildsModalVisible] = React.useState(false);

    const appStoreAvailableBuilds = props.appStoreAvailableBuilds ?? [];
    const currentlySelectedAppStoreBuilds = props.appStoreSelectedBuilds ?? [];
    const currentlySelectedAdditionalAppStoreBuildIds = props.additionalAppStoreSelectedBuilds ?? [];

    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, props.releaseId));
    const isStepInactive = props.stepStatus ? props.stepStatus == ReleaseStepStatus.Inactive : false;

    const currentlySelectedAdditionalAppStoreBuilds = appStoreAvailableBuilds.filter((build) =>
        currentlySelectedAdditionalAppStoreBuildIds.includes(build.appStoreBuild.buildNumber),
    );

    const isSubmitted = isAppSubmitted(
        props.appStoreType,
        props.appStoreState as AppleAppStoreState,
        props.reviewSubmissionState,
        props.appStoreState as GooglePlayStoreState,
        props.appStoreState as AmazonAppStoreState,
        props.appStoreState as HuaweiAppStoreState,
        props.appStoreState as SamsungAppStoreState,
        props.googleReleaseLifecycleState,
    );
    const changeSelectedBuildDisabled =
        !props.appStoreState || appStoreAvailableBuilds.length === 0 || isSubmitted || isStepInactive;
    const changeSelectedBuildButtonText =
        currentlySelectedAppStoreBuilds?.length > 0 ? 'Change selected build' : 'Select build';

    const titleText =
        isRollback ? 'Rollback build'
        : (
            isAppReleased(
                props.appStoreType,
                props.appStoreState as AppleAppStoreState,
                props.appStoreState as GooglePlayStoreState,
                props.appStoreState as AmazonAppStoreState,
                props.appStoreState as HuaweiAppStoreState,
                props.appStoreState as SamsungAppStoreState,
            )
        ) ?
            `Build released in ${displayStoreNameForAppStoreType(props.appStoreType)}`
        :   `Selected build in ${displayConsoleNameForAppStoreType(props.appStoreType)}`;

    const ciIntegrationName =
        currentlySelectedAppStoreBuilds && currentlySelectedAppStoreBuilds.length > 0 ?
            currentlySelectedAppStoreBuilds[0].associatedCIBuild ?
                displayNameForIntegrationType(currentlySelectedAppStoreBuilds[0].associatedCIBuild.integrationId)
            :   'CI'
        :   'CI';

    const isReleased = isAppReleased(
        props.appStoreType,
        props.appStoreState as AppleAppStoreState,
        props.appStoreState as GooglePlayStoreState,
        props.appStoreState as AmazonAppStoreState,
        props.appStoreState as HuaweiAppStoreState,
        props.appStoreState as SamsungAppStoreState,
    );

    let helperText: string;
    if (isRollback) {
        helperText = `The rollback build that ${isReleased ? 'was' : 'will be'} submitted for review.`;
    } else if (isReleased) {
        helperText = `The build released to the ${displayStoreNameForAppStoreType(
            props.appStoreType,
        )} and its associated build in ${ciIntegrationName}.`;
    } else {
        helperText = helperTextForAppStoreType(props.appStoreType, ciIntegrationName);
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ ...releaseStepModuleHeaderStyle }}>
                        <Space>
                            <Text style={{ ...releaseStepModuleTitleStyle }}>{titleText}</Text>
                            <TooltipInfoIcon tooltipContent={helperText} />
                        </Space>
                        {props.showChangeSelectedBuildCTA && !isRollback && (
                            <div>
                                {getIsGooglePlayStore(props.appStoreType) ?
                                    <Dropdown.Button
                                        menu={{
                                            items:
                                                getIsGooglePlayStore(props.appStoreType) ?
                                                    [
                                                        {
                                                            key: 'selectAdditionalBuilds',
                                                            label: 'Include build(s) from a previous release',
                                                            disabled: changeSelectedBuildDisabled,
                                                            onClick: () => setAdditionalBuildsModalVisible(true),
                                                        },
                                                    ]
                                                :   [],
                                        }}
                                        disabled={changeSelectedBuildDisabled}
                                        onClick={() => setSelectBuildModalVisible(true)}
                                    >
                                        {changeSelectedBuildButtonText}
                                    </Dropdown.Button>
                                :   <Button
                                        disabled={changeSelectedBuildDisabled}
                                        onClick={() => setSelectBuildModalVisible(true)}
                                    >
                                        {changeSelectedBuildButtonText}
                                    </Button>
                                }
                            </div>
                        )}
                    </div>
                    <Space direction="vertical" size={20} style={{ width: '100%', minWidth: 0 }}>
                        <AppStoreBuilds
                            platform={props.platform}
                            appStoreType={props.appStoreType}
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            isSubmitted={isSubmitted}
                            appStoreSelectedBuilds={currentlySelectedAppStoreBuilds}
                            additionalAppStoreSelectedBuilds={props.additionalAppStoreSelectedBuilds}
                            buildMatchingWarnings={props.buildMatchingWarnings}
                            willAutoPromotePreviousRelease={props.willAutoPromotePreviousRelease}
                        />
                    </Space>
                </div>
            </div>
            <ChangeSelectedBuildModal
                stepRunComponentProps={props.stepRunComponentProps}
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                availableBuilds={appStoreAvailableBuilds}
                platform={props.platform}
                appStoreType={props.appStoreType}
                currentlySelectedAppStoreBuilds={currentlySelectedAppStoreBuilds}
                open={selectBuildModalVisible}
                includeAssociatedCIBuilds={!isRollback}
                onClose={() => setSelectBuildModalVisible(false)}
                onSuccess={() => setSelectBuildModalVisible(false)}
            />
            <ChangeAdditionalBuildsModal
                platform={props.platform}
                appStoreType={props.appStoreType}
                includeAssociatedCIBuilds={!isRollback}
                open={additionalBuildsModalVisible}
                availableBuilds={appStoreAvailableBuilds}
                currentlySelectedAdditionalAppStoreBuilds={currentlySelectedAdditionalAppStoreBuilds}
                onClose={() => setAdditionalBuildsModalVisible(false)}
                onSuccess={() => setAdditionalBuildsModalVisible(false)}
                releaseId={props.releaseId}
                appId={props.appId}
                orgId={props.orgId}
                stepRunComponentProps={props.stepRunComponentProps}
            />
        </>
    );
};

export default SelectedAppStoreBuildModule;

const helperTextForAppStoreType: (appStoreType: AppStoreType, ciIntegrationName: string) => string = (
    appStoreType,
    ciIntegrationName,
) => {
    const defaultText = `The currently selected build in ${displayConsoleNameWithPrefix(
        appStoreType,
    )} and its associated build in ${ciIntegrationName}.`;
    switch (appStoreType) {
        case AppStoreType.Google:
            return `The currently selected build(s) in ${displayConsoleNameWithPrefix(
                appStoreType,
            )} and its associated build in ${ciIntegrationName}.`;
        case AppStoreType.Apple:
            return `${defaultText} Newly uploaded builds can take up to five minutes to show up in Runway after being uploaded to App Store Connect.`;
        case AppStoreType.Amazon:
            return defaultText;
        // intentionally left blank for Huawei as we can't select builds
        default:
            return null;
    }
};
